import { basicSetup, EditorView } from "codemirror";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { javascript } from "@codemirror/lang-javascript";

interface MountedCodeEditor extends HTMLElement {
    _codeEditorView?: EditorView;
}

function languageExtension(language: string) {
    if (language === "css") return css();
    if (language === "javascript" || language === "js") return javascript();
    return html();
}

function mount(host: MountedCodeEditor): void {
    if (host._codeEditorView) return;

    const inputId = host.dataset.codeEditorInput ?? "";
    const input = inputId !== "" ? document.getElementById(inputId) as HTMLTextAreaElement | null : null;
    if (!input) return;

    const mountEl = host.querySelector<HTMLElement>(".admin-code-editor__mount") ?? host;
    const language = host.dataset.codeEditorLanguage ?? "html";

    const view = new EditorView({
        doc: input.value,
        extensions: [
            basicSetup,
            languageExtension(language),
            EditorView.lineWrapping,
            EditorView.updateListener.of((update) => {
                if (update.docChanged) {
                    input.value = update.state.doc.toString();
                    input.dispatchEvent(new Event("input", { bubbles: true }));
                }
            }),
            EditorView.theme({
                "&": {
                    minHeight: "var(--code-editor-min-height, 18rem)",
                    fontSize: "13px",
                    borderRadius: "6px",
                },
                ".cm-scroller": {
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                },
                ".cm-content": {
                    padding: "10px 0",
                },
                ".cm-gutters": {
                    borderTopLeftRadius: "6px",
                    borderBottomLeftRadius: "6px",
                },
            }),
        ],
        parent: mountEl,
    });

    host._codeEditorView = view;
    input.addEventListener("cms:set-value", () => {
        const current = view.state.doc.toString();
        if (current === input.value) return;
        view.dispatch({
            changes: { from: 0, to: current.length, insert: input.value },
        });
    });
}

export const CodeEditor = {
    initAll(scope: Document | HTMLElement = document): void {
        scope.querySelectorAll<MountedCodeEditor>("[data-code-editor]").forEach(mount);
    },
};
